import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from "react";
import { routeSnapshotMeta } from "@japangolmok/content";
import { track } from "../analytics/events";
import { BrandMark } from "./BrandMark";

declare global {
  interface Window {
    __japangolmokOpenExternalURL?: (url: string) => Promise<void>;
  }
}

export const canonicalFor = (path: string) => `https://japangolmok.com${path}`;

export function usePageMeta(title: string, description: string, enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    document.title = title;
    document.querySelector<HTMLMetaElement>('meta[name="description"]')?.setAttribute("content", description);
    document.querySelector<HTMLMetaElement>('meta[property="og:title"]')?.setAttribute("content", title);
    document.querySelector<HTMLMetaElement>('meta[property="og:description"]')?.setAttribute("content", description);
    document.querySelector<HTMLMetaElement>('meta[property="og:url"]')?.setAttribute("content", canonicalFor(window.location.pathname));
    document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.setAttribute("href", canonicalFor(window.location.pathname));
  }, [title, description, enabled]);
}

export function ExternalLink({ href, children, className = "", surface = "source", style, ariaLabel }: { href: string; children: ReactNode; className?: string; surface?: string; style?: CSSProperties; ariaLabel?: string }) {
  const openInToss = window.__japangolmokOpenExternalURL;
  const isToss = document.body.dataset.appChannel === "apps-in-toss" || Boolean(openInToss);
  const isPhone = href.startsWith("tel:");
  const copyValue = isPhone ? href.slice(4) : href;
  const [failed, setFailed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const recovery = useRef<HTMLDivElement>(null);
  const address = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!failed) return;
    recovery.current?.scrollIntoView?.({ block: "nearest" });
    address.current?.focus();
    address.current?.select();
  }, [failed]);
  const openExternal = () => {
    if (!openInToss) { setFailed(true); return; }
    let opening: Promise<void>;
    try { opening = openInToss(href); } catch { setFailed(true); return; }
    setFailed(false);
    setCopied(false);
    setCopyFailed(false);
    try { track("official_source_open", { surface }); } catch { /* A failed metric must not open an unhandled WebView window. */ }
    void opening.catch(() => setFailed(true));
  };

  const copyAddress = async () => {
    try { await navigator.clipboard.writeText(copyValue); setCopied(true); setCopyFailed(false); } catch { setCopied(false); setCopyFailed(true); address.current?.focus(); address.current?.select(); }
  };
  const label = <>{children}<span aria-hidden="true"> ↗</span><span className="sr-only"> {isPhone ? "전화 앱" : isToss ? "외부 페이지" : "새 창"}</span></>;
  const onTossKeyDown = (event: KeyboardEvent<HTMLAnchorElement>) => {
    if (event.key === "Enter" || event.key === " ") { event.preventDefault(); openExternal(); }
  };
  return <>{isToss
    ? <a className={className} style={{ cursor: "pointer", ...style }} aria-label={ariaLabel} role="link" tabIndex={0} onClick={openExternal} onKeyDown={onTossKeyDown}>{label}</a>
    : <a className={className} href={href} style={style} aria-label={ariaLabel} target={isPhone ? undefined : "_blank"} rel="noreferrer" onClick={() => track("official_source_open", { surface })}>{label}</a>}
    {failed && <div className="external-link-recovery" role="status" ref={recovery}><span>{isPhone ? "전화 앱을 열지 못했어요. 번호를 복사해 직접 걸어 주세요." : "링크가 열리지 않았어요. 주소를 복사해 브라우저에서 확인해 주세요."}</span><input ref={address} aria-label={isPhone ? "전화번호" : "열리지 않은 링크 주소"} readOnly value={copyValue} onFocus={(event) => event.currentTarget.select()} /><button type="button" onClick={openExternal}>{isPhone ? "전화 앱 다시 열기" : "링크 다시 열기"}</button><button type="button" onClick={() => void copyAddress()}>{copied ? isPhone ? "번호를 복사했어요" : "주소를 복사했어요" : isPhone ? "번호 복사" : "주소 복사"}</button>{copyFailed && <span>자동 복사가 되지 않았어요. 선택된 {isPhone ? "번호" : "주소"}를 길게 눌러 직접 복사해 주세요.</span>}</div>}</>;
}

export function SourceStamp({ source = "공식 공항 정보", checkedAt = routeSnapshotMeta.checkedAt }: { source?: string; checkedAt?: string }) {
  const date = new Intl.DateTimeFormat("ko-KR", { timeZone: "Asia/Seoul", dateStyle: "medium" }).format(new Date(checkedAt));
  return <p className="source-stamp"><span>{source}</span><time dateTime={checkedAt}>확인 {date}</time></p>;
}

export function Layout({ children }: { children: ReactNode }) {
  const path = window.location.pathname;
  const mobileNav = useRef<HTMLElement>(null);
  useEffect(() => {
    const nav = mobileNav.current;
    if (!nav || typeof ResizeObserver === "undefined") return;
    const update = () => document.documentElement.style.setProperty("--mobile-nav-height", `${Math.ceil(nav.getBoundingClientRect().height)}px`);
    const observer = new ResizeObserver(update); observer.observe(nav); update();
    return () => observer.disconnect();
  }, []);
  const isCurrent = (href: string) => {
    if (href === "/") return path === "/";
    if (href === "/flights") return path === "/flights" || path.startsWith("/from/") || path.startsWith("/airports/");
    if (href === "/today") return path === "/today" || /\/trips\/[^/]+\/today$/.test(path);
    if (href === "/trips" && /\/trips\/[^/]+\/today$/.test(path)) return false;
    if (["/trips", "/cards", "/walks", "/companions"].includes(href)) return path === href || path.startsWith(`${href}/`);
    return path === href;
  };
  const currentProps = (href: string) => isCurrent(href)
    ? { className: "is-current", "aria-current": "page" as const }
    : {};
  return (
    <>
      <header className="site-header">
        <a className="brand" href="/" aria-label="일본골목 홈"><BrandMark /><span><strong>일본골목</strong><small>여행의 시작부터 돌아온 뒤까지</small></span></a>
        <nav aria-label="주요 메뉴"><a href="/flights" {...currentProps("/flights")}>직항</a><a href="/walks" {...currentProps("/walks")}>코스</a><a href="/trips" {...currentProps("/trips")}>여행</a><a href="/today" {...currentProps("/today")}>오늘</a></nav>
      </header>
      <main id="main">{children}</main>
      <footer>
        <div><strong>일본골목</strong><p>여행의 판단과 기록을 이어 주되, 예약·발권·실시간 길찾기는 공식 운영사에서 마지막으로 확인해요.</p></div>
        <nav aria-label="추가 여행 도구"><a href="/companions" {...currentProps("/companions")}>동행</a><a href="/cards" {...currentProps("/cards")}>도착카드</a></nav>
        <nav aria-label="정책 및 지원"><a href="/changes" {...currentProps("/changes")}>변경 기록</a><a href="/status" {...currentProps("/status")}>정보 상태</a><a href="/correct" {...currentProps("/correct")}>정정 제보</a><a href="/privacy" {...currentProps("/privacy")}>개인정보</a><a href="/terms" {...currentProps("/terms")}>이용약관</a><a href="/support" {...currentProps("/support")}>문의</a></nav>
      </footer>
      <nav ref={mobileNav} className="mobile-nav" aria-label="모바일 주요 메뉴"><a href="/flights" {...currentProps("/flights")}>직항</a><a href="/walks" {...currentProps("/walks")}>코스</a><a href="/trips" {...currentProps("/trips")}>여행</a><a href="/today" {...currentProps("/today")}>오늘</a></nav>
    </>
  );
}
